// client/src/components/contratos/Contratos.js
import React, { useState, useCallback, useMemo, Fragment } from 'react';
import { useContratos, useMutateContratos, useCancelarContrato } from '../../hooks/useContratos';
import { useTiposContratoAtivos } from '../../hooks/useTiposContrato';
import ContratoForm from './ContratoForm';
import ContratoRow from './ContratoRow';
import ContratoPreview from './ContratoPreview';
import FiltroContratos from './FiltroContratos';
import AssinarContratoModal from './AssinarContratoModal';
import ConfirmarRetiradaModal from './ConfirmarRetiradaModal';
import GerarLinkContratoModal from './GerarLinkContratoModal';
import { PlusCircle, Filter as FilterIcon, Loader2, FileText, Eye, SquarePen, Trash2, XCircle, PenLine, PackageCheck, Ban, Link2 } from 'lucide-react';
import Pagination from '../common/Pagination';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';

const Contratos = () => {
    const [showForm, setShowForm] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState({});
    const [currentContrato, setCurrentContrato] = useState(null);
    const [previewContrato, setPreviewContrato] = useState(null);
    const [selectedContrato, setSelectedContrato] = useState(null);
    const [assinarContratoAlvo, setAssinarContratoAlvo] = useState(null);
    const [retiradaContratoAlvo, setRetiradaContratoAlvo] = useState(null);
    const [linkContratoAlvo, setLinkContratoAlvo] = useState(null);
    const itemsPerPage = 10;

    const queryParams = useMemo(() => ({
        page: currentPage, limit: itemsPerPage, ...activeFilters
    }), [currentPage, activeFilters]);

    // ✅ BUSCANDO APENAS OS DADOS NECESSÁRIOS
    const { data: contratosData, isLoading, refetch } = useContratos(queryParams);
    const { deleteContrato, isPending: isDeleting } = useMutateContratos();
    const cancelarContrato = useCancelarContrato();

    // ✅ APENAS TIPOS ATIVOS PARA FORMULÁRIOS
    const { data: tiposContrato = [] } = useTiposContratoAtivos();

    const contratos = contratosData?.data || [];
    const count = contratosData?.count || 0;
    const pagination = contratosData?.pagination || {};

    const handleAdicionar = useCallback(() => {
        setCurrentContrato(null);
        setShowForm(true);
        setShowPreview(false);
    }, []);

    const handleEdit = useCallback((contrato) => {
        setCurrentContrato(contrato);
        setShowForm(true);
        setShowPreview(false);
        setSelectedContrato(null);
    }, []);

    const handleView = useCallback((contrato) => {
        setPreviewContrato(contrato);
        setShowPreview(true);
        setShowForm(false);
        setSelectedContrato(null);
    }, []);

    const handleCloseForm = useCallback(() => {
        setShowForm(false);
        setCurrentContrato(null);
    }, []);

    const handleClosePreview = useCallback(() => {
        setShowPreview(false);
        setPreviewContrato(null);
    }, []);

    const handleFiltrosClick = useCallback(() => {
        setShowFilters(p => !p);
        if (showForm || showPreview) {
            setShowForm(false);
            setShowPreview(false);
        }
    }, [showForm, showPreview]);

    const paginate = (page) => setCurrentPage(page);

    // Contraparte pode ser Cliente/Fornecedor/Transportador (Contrato.modeloContraparte,
    // refPath polimórfico) — só Cliente tem subdocumento .user, os outros dois
    // guardam nome direto no próprio documento.
    const getContraparteNome = (contrato) => {
        const c = contrato?.cliente;
        if (!c || typeof c !== 'object') return '';
        if (contrato.modeloContraparte === 'Fornecedor') return c.nomeFantasia || c.razaoSocial || '';
        if (contrato.modeloContraparte === 'Transportador') return c.nomeCompleto || c.razaoSocial || c.nomeFantasia || '';
        return c.user?.name || c.nome || '';
    };

    const handleDelete = useCallback((id, numero) => {
        if (window.confirm(`Apagar Contrato Nº ${numero}?`)) {
            deleteContrato.mutate(id);
            setSelectedContrato(null);
        }
    }, [deleteContrato]);

    const handleAssinar = useCallback((contrato) => {
        setAssinarContratoAlvo(contrato);
        setSelectedContrato(null);
    }, []);

    const handleConfirmarRetirada = useCallback((contrato) => {
        setRetiradaContratoAlvo(contrato);
        setSelectedContrato(null);
    }, []);

    const handleGerarLink = useCallback((contrato) => {
        setLinkContratoAlvo(contrato);
        setSelectedContrato(null);
    }, []);

    const handleCancelar = useCallback((contrato) => {
        if (window.confirm(`Cancelar o Contrato Nº ${contrato.numeroContrato}? Esta ação não pode ser desfeita.`)) {
            cancelarContrato.mutate({ id: contrato._id });
            setSelectedContrato(null);
        }
    }, [cancelarContrato]);

    const handleSaveSuccess = useCallback(() => {
        handleCloseForm();
        refetch();
    }, [handleCloseForm, refetch]);

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<FileText />}
                    title={showPreview ? 'Visualização do Contrato' : 'Gerenciamento de Contratos'}
                    actions={<>
                        {!showForm && !showPreview && (
                            <>
                                <button
                                    onClick={handleFiltrosClick}
                                    className="action-button action-button-secondary"
                                >
                                    <FilterIcon size={18} className="icon"/>
                                    {showFilters ? 'Ocultar Filtros' : 'Exibir Filtros'}
                                </button>
                            </>
                        )}
                        {(showForm || showPreview) && (
                            <button onClick={showPreview ? handleClosePreview : handleCloseForm} className="action-button action-button-secondary">
                                <XCircle size={18} className="icon"/> Fechar
                            </button>
                        )}
                    </>}
                />

                {/* Filtros */}
                {showFilters && !showForm && !showPreview && (
                    <div className="content-panel">
                        <h3 className="panel-title">Filtrar Contratos</h3>
                        <FiltroContratos onFilterSubmit={setActiveFilters} loading={isLoading} />
                    </div>
                )}
                
                {/* Formulário de Contrato */}
                {showForm && 
                    <div className="content-panel">
                        <ContratoForm 
                            current={currentContrato} 
                            onCloseForm={handleCloseForm} 
                            onSaveSuccess={handleSaveSuccess}
                            tiposContrato={tiposContrato}
                        />
                    </div>
                }

                {/* Preview do Contrato - ✅ AGORA PASSA O TEMPLATE CORRETAMENTE */}
                {showPreview && previewContrato && 
                    <div className="content-panel">
                        <ContratoPreview
                            contrato={previewContrato}
                            onClose={handleClosePreview}
                        />
                    </div>
                }
            </div>
            
            {/* Lista de Contratos */}
            {!showForm && !showPreview && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading && contratos.length === 0 ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin"/>
                            </div>
                        ) : (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Nº Contrato</th>
                                        <th>Cliente</th>
                                        <th>Tipo</th>
                                        <th>Status</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contratos.length > 0 ? (
                                        contratos.map(c => (
                                            <ContratoRow
                                                key={c._id}
                                                contrato={c}
                                                onOpenActions={setSelectedContrato}
                                            />
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="td-empty">
                                                Nenhum contrato encontrado.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                    {pagination && count > itemsPerPage && !isLoading && (
                        <Pagination 
                            {...pagination} 
                            totalItems={count} 
                            onPageChange={paginate} 
                            currentPage={currentPage} 
                        />
                    )}
                </div>
            )}

            <FabButton
                onClick={handleAdicionar}
                title="Adicionar Contrato"
                visible={!showForm && !showPreview}
                icon={<FileText size={26} className="icon"/>}
            />

            <ActionsDrawer
                open={!!selectedContrato}
                onClose={() => setSelectedContrato(null)}
                title={selectedContrato?.numeroContrato ? `Contrato Nº ${selectedContrato.numeroContrato}` : 'Contrato'}
                subtitle={getContraparteNome(selectedContrato)}
                actions={selectedContrato ? [
                    {
                        key: 'visualizar',
                        label: 'Visualizar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleView(selectedContrato),
                    },
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <SquarePen size={20} className="icon" />,
                        onClick: () => handleEdit(selectedContrato),
                    },
                    ...(selectedContrato.statusContrato !== 'Cancelado' ? [{
                        key: 'assinar',
                        label: 'Assinar',
                        icon: <PenLine size={20} className="icon" />,
                        onClick: () => handleAssinar(selectedContrato),
                    }] : []),
                    ...(selectedContrato.statusContrato === 'Em Elaboração'
                        && !(selectedContrato.assinaturas || []).some((a) => a.papel === 'contraparte') ? [{
                        key: 'gerar-link',
                        label: 'Gerar Link de Assinatura',
                        icon: <Link2 size={20} className="icon" />,
                        onClick: () => handleGerarLink(selectedContrato),
                    }] : []),
                    ...(selectedContrato.statusContrato !== 'Cancelado' && selectedContrato.modeloContraparte === 'Transportador' ? [{
                        key: 'confirmar-retirada',
                        label: 'Confirmar Retirada',
                        icon: <PackageCheck size={20} className="icon" />,
                        onClick: () => handleConfirmarRetirada(selectedContrato),
                    }] : []),
                    ...(selectedContrato.statusContrato !== 'Cancelado' ? [{
                        key: 'cancelar',
                        label: 'Cancelar Contrato',
                        icon: <Ban size={20} className="icon" />,
                        onClick: () => handleCancelar(selectedContrato),
                        destructive: true,
                        loading: cancelarContrato.isPending,
                    }] : []),
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedContrato._id, selectedContrato.numeroContrato),
                        destructive: true,
                        loading: isDeleting,
                    },
                ] : []}
            />

            {assinarContratoAlvo && (
                <AssinarContratoModal contrato={assinarContratoAlvo} onClose={() => setAssinarContratoAlvo(null)} />
            )}

            {retiradaContratoAlvo && (
                <ConfirmarRetiradaModal contrato={retiradaContratoAlvo} onClose={() => setRetiradaContratoAlvo(null)} />
            )}

            {linkContratoAlvo && (
                <GerarLinkContratoModal contrato={linkContratoAlvo} onClose={() => setLinkContratoAlvo(null)} />
            )}
        </Fragment>
    );
};

export default Contratos;